import * as React from "react";
import { ArrowDown } from "lucide-react";
import { cn } from "@finster/ui";

/**
 * Floating "return to bottom" control for chat history that has stopped
 * auto-scrolling because the user scrolled up. Button styling matches
 * finster-chat's `ConversationStreamIndicator`; placement is bottom-right
 * inside the scroll viewport (Claude Excel add-in pattern) so the affordance
 * stays out of the message reading lane in a narrow taskpane.
 *
 * Keep mounted at all times (toggle `visible` instead of conditional render)
 * so showing/hiding does not change scroll height and jump the viewport.
 */
interface JumpToLatestButtonProps {
  onClick: () => void;
  visible?: boolean;
  className?: string;
}

export const JumpToLatestButton: React.FC<JumpToLatestButtonProps> = ({
  onClick,
  visible = true,
  className,
}) => (
  <div
    aria-hidden={!visible}
    className={cn(
      "pointer-events-none sticky bottom-1 z-10 flex shrink-0 justify-end pr-1 pt-1",
      !visible && "invisible",
      className,
    )}
  >
    <button
      type="button"
      onClick={onClick}
      aria-label="Return to bottom"
      tabIndex={visible ? 0 : -1}
      className={cn(
        "flex size-9 items-center justify-center",
        "rounded-full border border-border bg-background",
        "text-muted-foreground shadow-sm",
        "transition-colors hover:bg-ghost-hover hover:text-foreground",
        visible ? "pointer-events-auto cursor-pointer" : "pointer-events-none",
      )}
    >
      <ArrowDown className="size-5" />
    </button>
  </div>
);
